import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faCode,
  faCog,
  faEnvelope,
  faExclamationCircle,
  faEye,
  faLink,
  faMagic,
  faPlus,
  faRefresh,
  faTableColumns,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { faPaperPlane as faSend } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import QRCode from "qrcode";
import Select from "react-select";
import Editor from "@monaco-editor/react";
import { domainConfig } from "../config.js";
import { DEFAULT_TEMPLATES } from "../utils/emailTemplates.js";
import { getEventRsvpSettings } from "../api/rsvp.js";
import { emailRsvpDefinitionIds, emailRsvpTemplateErrorCode, removeEmailRsvpLinks, rsvpLinkPlaceholder } from "../utils/rsvpAnswers.js";
import EventRsvpSettingsModal from "./EventRsvpSettingsModal.js";
import EventRsvpManagerModal from "./EventRsvpManagerModal.js";
import useAuth from "../hooks/useAuth.js";
import { canAccessEventRsvps } from "../utils/eventPermissions.js";
import { HelpTooltip } from "../assets/functions.js";
import { ShowConfirm } from "./Alerts.js";
import DropzoneComponent from "./Dropzone.js";
import NotyfContext from "../contexts/NotyfContext.js";

const getTemplateVariablesObject = (variables) => {
  if (variables && typeof variables === "object" && !Array.isArray(variables)) {
    return variables;
  }

  return {};
};

const normalizeRsvpMetadata = (value) => {
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (_) {
      return [];
    }
  }
  return Array.isArray(parsed) ? parsed.filter((item) => item?.definition_id && item.presence !== false) : [];
};

const getPreviewContentHash = (value) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }

  return hash.toString(36);
};

const SYSTEM_GENERIC_VARIABLES = [
  { label: "Logo do modelo", value: "{{logo}}" },
];

// ─── EmailTemplateEditorModal ─────────────────────────────────────────────────
const EmailTemplateEditorModal = ({
  show,
  onHide,
  mode = "event",
  template,
  templates,
  sourceTemplates = [],
  userTypes = [],
  onSave,
  onRsvpChanged,
  dataTable,
  saving = false,
}) => {
  const notyf = useContext(NotyfContext);
  const isSystemMode = mode === "system";
  const userState = useAuth();
  const canManageRsvps = !isSystemMode && canAccessEventRsvps(userState, dataTable?.platform);
  const isEdit = isSystemMode || Boolean(template);
  const INITIAL = useMemo(() => ({
    template_id: null,
    name: "",
    subject: "",
    from_name: "",
    reply_to: "",
    qrcode: false,
    logo: "",
    html: "",
    template_version: 0.0,
    variables: null,
  }), []);

  const normalizeSystemTemplate = useCallback((value) => ({
    name: value?.name || "",
    subject: value?.subject || "",
    from_name: value?.from_name || "",
    reply_to: value?.reply_to || "",
    html: value?.html || "",
  }), []);

  const normalizeSystemTemplates = useCallback((value) => (
    userTypes.reduce((result, typeConfig) => {
      const type = String(typeConfig.type);
      result[type] = normalizeSystemTemplate(value?.[type] || value?.[typeConfig.type]);
      return result;
    }, {})
  ), [normalizeSystemTemplate, userTypes]);

  const [selectedSystemType, setSelectedSystemType] = useState(() => String(userTypes[0]?.type ?? "0"));
  const [systemDrafts, setSystemDrafts] = useState({});
  const validationSchema = useMemo(() => Yup.object({
    name: Yup.string().required("Campo obrigatório"),
    subject: Yup.string().required("Campo obrigatório"),
    from_name: Yup.string().required("Campo obrigatório"),
    reply_to: Yup.string().email("Insira um e-mail válido").required("Campo obrigatório"),
    html: Yup.string()
      .min(50, "O HTML deve conter pelo menos 50 caracteres")
      .required("O contéudo HTML não pode ser vazio"),
  }), []);

  const formik = useFormik({
    initialValues: template || INITIAL,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      if (!validateRsvpTemplate(values.html)) return;
      if (isSystemMode) {
        const safeTemplate = {
          name: values.name || "",
          subject: values.subject || "",
          from_name: values.from_name || "",
          reply_to: values.reply_to || "",
          html: values.html || "",
        };
        const nextDrafts = normalizeSystemTemplates({
          ...systemDrafts,
          [selectedSystemType]: safeTemplate,
        });
        onSave({ templates: nextDrafts });
        return;
      }

      const { logo, ...restValues } = values;
      const nextVariables = {
        ...getTemplateVariablesObject(values.variables),
      };

      if (logo) {
        nextVariables.logo = logo;
      } else {
        delete nextVariables.logo;
      }

      const hasQrCode = Boolean(restValues.html && /\{\{qrcode/i.test(restValues.html));
      const linkedRsvpMetadata = getLinkedRsvpMetadata(restValues.html);
      onSave({
        ...restValues,
        event_id: dataTable.evento_id,
        qrcode: hasQrCode ? 1 : 0,
        variables: Object.keys(nextVariables).length > 0 ? nextVariables : null,
        rsvp_active: linkedRsvpMetadata.length > 0 ? 1 : 0,
        rsvp_metadata: linkedRsvpMetadata.length > 0 ? JSON.stringify(linkedRsvpMetadata.map((item) => (
          item.config_revision || item.confirmationPage || item.fieldValue
            ? item
            : { definition_id: item.definition_id, placement_id: item.placement_id, field: item.field, buttonText: item.buttonText, presence_reminder: item.presence_reminder === true }
        ))) : null
      });
    },
  });

  const form = formik.values;
  const [activePane, setActivePane] = useState("split");
  const [selectedSourceTemplate, setSelectedSourceTemplate] = useState(null);
  const sourceTemplateOptions = useMemo(() => isSystemMode ? [
    { value: "new", label: "Modelo em branco" },
    ...DEFAULT_TEMPLATES.map((preset, index) => ({ value: index, label: preset.nome })),
  ] : sourceTemplates.map((source) => ({
    value: String(source.template_id),
    label: source.name || "Sem título",
    template: source,
  })), [isSystemMode, sourceTemplates]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (formik.submitCount > 0 && (formik.errors.subject || formik.errors.from_name || formik.errors.reply_to)) {
      setShowAdvanced(true);
    }
  }, [formik.submitCount, formik.errors]);

  // RSVP Form State
  // Saved link placements in the email draft
  const [rsvpList, setRsvpList] = useState([]);
  const [eventRsvpRegistry, setEventRsvpRegistry] = useState({ definitions: [] });
  const [showRsvpPicker, setShowRsvpPicker] = useState(false);
  const [showRsvpCreation, setShowRsvpCreation] = useState(false);
  const [showRsvpManager, setShowRsvpManager] = useState(false);
  const [rsvpCreationSequence, setRsvpCreationSequence] = useState(0);
  const [rsvpRegistryReload, setRsvpRegistryReload] = useState(0);
  const [rsvpRegistryLoading, setRsvpRegistryLoading] = useState(false);
  const [rsvpRegistryError, setRsvpRegistryError] = useState("");
  const pendingRsvpInsertionRef = useRef(null);

  const editorRef = useRef(null);

  // Insert variable at cursor position inside the editor
  const insertVariable = (variable) => {
    const editor = activePane === "preview" ? null : editorRef.current;
    if (!editor || !editor.getModel()) {
      formik.setFieldValue("html", `${formik.values.html || ""}${variable}`);
      return;
    }
    const selection = editor.getSelection();
    if (selection) {
      editor.executeEdits("insertVariable", [
        { range: selection, text: variable, forceMoveMarkers: true }
      ]);
    } else {
      const position = editor.getPosition() || { lineNumber: 1, column: 1 };
      editor.executeEdits("insertVariable", [
        {
          range: {
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          },
          text: variable,
          forceMoveMarkers: true,
        },
      ]);
    }
    editor.focus();
  };

  const variableOptions = !isSystemMode ? dataTable?.json?.form?.fields?.map((f) => {
    const varValue = f.inputID || f.name;
    return {
      label: f.inputID ? `${f.label} (${f.inputID})` : f.label,
      value: `{{${varValue}}}`,
    };
  }) || [] : [];

  // Test Email Modal State
  const [showTestModal, setShowTestModal] = useState(false);

  useEffect(() => {
    setShowRsvpPicker(false);
    setShowRsvpCreation(false);
    setShowRsvpManager(false);
    setEventRsvpRegistry({ definitions: [] });
    setRsvpRegistryError("");
    pendingRsvpInsertionRef.current = null;
  }, [dataTable?.evento_id, isSystemMode, show]);

  useEffect(() => {
    if (!show || isSystemMode || !dataTable?.evento_id) return undefined;
    const controller = new AbortController();
    setRsvpRegistryLoading(true);
    setRsvpRegistryError("");
    getEventRsvpSettings({ eventId: dataTable.evento_id, signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        const registry = response.data?.registry || { definitions: response.data?.settings ? [response.data.settings] : [] };
        const available = registry.definitions || [];
        const pending = pendingRsvpInsertionRef.current;
        const createdId = String(pending?.eventId) === String(dataTable.evento_id) ? pending.definitionId : null;
        setEventRsvpRegistry(registry);
        if (createdId && !available.some((item) => item.definition_id === createdId)) {
          setRsvpRegistryError("O RSVP foi salvo, mas ainda não apareceu na lista atualizada. Tente atualizar a lista novamente para inserir o link.");
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted && error?.code !== "ERR_CANCELED") {
          setRsvpRegistryError("Não foi possível atualizar os RSVPs do evento. Tente novamente.");
        }
      })
      .finally(() => { if (!controller.signal.aborted) setRsvpRegistryLoading(false); });
    return () => controller.abort();
  }, [dataTable?.evento_id, isSystemMode, rsvpRegistryReload, show]);

  const addPresencePlaceholder = useCallback((settings) => {
    if (!settings?.definition_id) return false;
    const item = { presence: true, definition_id: settings.definition_id, field: settings.name || "RSVP" };
    setRsvpList((current) => current.some((entry) => entry.definition_id === item.definition_id) ? current : [...current, item]);
    insertVariable(rsvpLinkPlaceholder(settings.definition_id));
    notyf.success("Link RSVP inserido no editor!");
    return true;
  }, [insertVariable, notyf]);

  const removeRsvp = (definition) => {
    if (saving) return;
    const editor = activePane === "preview" ? null : editorRef.current;
    const model = editor?.getModel();
    const html = model ? editor.getValue() : formik.values.html;
    const nextHtml = removeEmailRsvpLinks(html, definition.definition_id);
    if (model && nextHtml !== html) {
      editor.pushUndoStop();
      editor.executeEdits("removeRsvpLink", [{ range: model.getFullModelRange(), text: nextHtml, forceMoveMarkers: true }]);
      editor.pushUndoStop();
    }
    formik.setFieldValue("html", nextHtml);
    notyf.success("Link RSVP removido deste modelo. Salve para confirmar a alteração.");
  };

  useEffect(() => {
    const pending = pendingRsvpInsertionRef.current;
    if (!show || isSystemMode || !pending || String(pending.eventId) !== String(dataTable?.evento_id)) return;
    const settings = eventRsvpRegistry.definitions.find((item) => item.definition_id === pending.definitionId);
    if (!settings) return;
    pendingRsvpInsertionRef.current = null;
    addPresencePlaceholder(settings);
  }, [addPresencePlaceholder, dataTable?.evento_id, eventRsvpRegistry.definitions, isSystemMode, show]);

  const openRsvpPicker = () => {
    setShowRsvpPicker(true);
  };

  const requestRsvpCreation = () => {
    setShowRsvpPicker(false);
    setRsvpCreationSequence((value) => value + 1);
    setShowRsvpCreation(true);
  };

  const insertSelectedRsvp = (settings) => {
    if (addPresencePlaceholder(settings)) setShowRsvpPicker(false);
  };

  const getLinkedRsvpMetadata = (html) => {
    const definitionIds = emailRsvpDefinitionIds(html);
    return [
      ...rsvpList.filter((item) => definitionIds.includes(item.definition_id)),
      ...definitionIds.filter((id) => !rsvpList.some((item) => item.definition_id === id)).map((id) => ({
        definition_id: id,
        field: eventRsvpRegistry.definitions.find((item) => item.definition_id === id)?.name || id,
      })),
    ];
  };
  const emailRsvpEntries = emailRsvpDefinitionIds(form.html).map((id) => ({
    definition_id: id,
    field: eventRsvpRegistry.definitions.find((item) => item.definition_id === id)?.name
      || rsvpList.find((item) => item.definition_id === id)?.field || id,
  }));
  const rsvpVariables = eventRsvpRegistry.definitions.map((item) => ({ label: "Link RSVP: " + item.name, value: rsvpLinkPlaceholder(item.definition_id) }));

  const eventVariables = [
    { label: "E-mail do participante", value: "{{email}}" },
    { label: "Logo do modelo", value: "{{logo}}" },
    {
      label: "Código QR (Informação do participante)",
      value: '<img src="{{qrcode}}" alt="Código QR" width="180" height="180" style="display: block; margin: 0 auto; border: 0;" />',
    },
    {
      label: "Link direto do Código QR (Informação do participante)",
      value: "{{qrcode_download_url}}",
    },
    {
      label: "Código QR (Login automatico no Networking)",
      value: '<a href="{{qrcode_with_user_access_code_url}}" target="_blank"><img src="{{qrcode_with_user_access_code}}" alt="Código QR Networking" width="180" height="180" style="display: block; margin: 0 auto; border: 0;" /></a>',
    },
    {
      label: "Link direto (Login automático no networking)",
      value: "{{qrcode_with_user_access_code_url}}",
    },
    { label: "Código de acesso", value: "{{user_access_code}}" },
    ...variableOptions,
    ...rsvpVariables,
    { label: "+ Configurar novo link RSVP...", value: "__new_rsvp__" },
  ];
  const combinedVariables = isSystemMode ? SYSTEM_GENERIC_VARIABLES : eventVariables;

  const resetState = useCallback(() => {
    formik.resetForm({ values: INITIAL });
    setActivePane("split");
    setSelectedSourceTemplate(null);
    setShowAdvanced(false);
    setRsvpList([]);
    setShowTestModal(false);
  }, [formik, INITIAL]);

  const handleSystemTypeChange = (type) => {
    const nextType = String(type);
    if (!isSystemMode || nextType === selectedSystemType) return;

    const nextDrafts = {
      ...systemDrafts,
      [selectedSystemType]: normalizeSystemTemplate(formik.values),
    };
    setSystemDrafts(nextDrafts);
    setSelectedSystemType(nextType);
    formik.setValues(normalizeSystemTemplate(nextDrafts[nextType]));
    formik.setTouched({});
    setSelectedSourceTemplate(null);
    setShowAdvanced(false);
  };

  useEffect(() => {
    if (show) {
      if (isSystemMode) {
        const normalizedTemplates = normalizeSystemTemplates(templates);
        const firstType = String(userTypes[0]?.type ?? "0");
        setSystemDrafts(normalizedTemplates);
        setSelectedSystemType(firstType);
        formik.setValues(normalizedTemplates[firstType] || INITIAL);
        setRsvpList([]);
        setSelectedSourceTemplate(null);
        setShowAdvanced(false);
      } else if (template) {
        const templateVariables = getTemplateVariablesObject(template.variables);
        formik.setValues({
          template_id: template.template_id,
          name: template.name || "",
          subject: template.subject || "",
          from_name: template.from_name || "",
          reply_to: template.reply_to || "",
          qrcode: Boolean(template.qrcode),
          logo: templateVariables.logo || "",
          html: template.html || "",
          template_version: template.template_version || 0.0,
          variables: template.variables || null,
        });

        setRsvpList(normalizeRsvpMetadata(template.rsvp_metadata));
        setShowAdvanced(false);
      } else {
        resetState();
      }
      setSelectedSourceTemplate(null);
      setActivePane("split");
    }

    // Inject reliable HTML beautifier script silently via Local Public folder
    if (!document.getElementById("js-beautify-local")) {
      const script = document.createElement("script");
      script.id = "js-beautify-local";
      script.src = "/beautify-html.min.js";
      script.async = true;
      document.body.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, template, isSystemMode]);

  const handleChange = (field, value) => {
    formik.setFieldValue(field, value);
    if (isSystemMode) {
      setSystemDrafts((current) => ({
        ...current,
        [selectedSystemType]: {
          ...normalizeSystemTemplate(current[selectedSystemType]),
          [field]: value,
        },
      }));
    }
  };

  const [previewVersion, setPreviewVersion] = useState(0);
  const [htmlValidationErrors, setHtmlValidationErrors] = useState([]);
  const templateLogoUrl = form.logo && !isSystemMode && dataTable?.evento_id
    ? `${domainConfig.imageServer}/plataforma-eventos/${dataTable.evento_id}/${form.logo}`
    : `${domainConfig.imageServer}/empty-background.png`;

  const handleLogoPreviewSuccess = (filename, setFieldValue, field) => {
    setFieldValue(field, filename);
  };

  const handleLogoPreviewError = (error) => {
    notyf.open({
      type: "danger",
      message: error,
      ripple: true,
      dismissible: true,
    });
  };

  function handleEditorDidMount(editor, monaco) {
    editorRef.current = editor;

    monaco.editor.onDidChangeMarkers(() => {
      const model = editor.getModel();
      if (model) {
        const markers = monaco.editor.getModelMarkers({ resource: model.uri });
        setHtmlValidationErrors(markers);
      }
    });
  }

  const validateRsvpTemplate = (html) => {
    const code = emailRsvpTemplateErrorCode(html);
    const messages = {
      RSVP_TEMPLATE_PLACEHOLDER_INVALID: "Há um placeholder RSVP inválido no modelo. Remova-o e insira o link pelo seletor de RSVP.",
      RSVP_TEMPLATE_PREVIEW_LINK: "Links de prévia RSVP não podem ser salvos no modelo. Insira o link pelo seletor de RSVP.",
      RSVP_TEMPLATE_CONTAINS_CREDENTIAL: "Links pessoais de participantes não podem ser salvos no modelo. Insira o link pelo seletor de RSVP.",
      RSVP_TEMPLATE_LEGACY_LINK: "Este modelo contém um link RSVP antigo. Substitua-o pelo placeholder do RSVP deste evento, preservando o texto e o estilo do botão.",
    };
    let message = messages[code];
    const ids = emailRsvpDefinitionIds(html);
    if (!message && ids.length) {
      if (isSystemMode) message = "Links RSVP precisam de um evento. Configure-os no modelo de e-mail do evento.";
      else if (rsvpRegistryLoading || rsvpRegistryError) message = "Aguarde ou atualize a lista de RSVPs do evento antes de salvar.";
      else if (ids.some((id) => !eventRsvpRegistry.definitions.some((definition) => definition.definition_id === id))) {
        message = "Um link RSVP não pertence a este evento ou foi removido. Substitua-o pelo seletor de RSVP.";
      }
    }
    if (message) notyf.error(message);
    return !message;
  };

  const handleSubmit = async () => {
    // Sync Monaco value just in case
    const htmlValue = editorRef.current ? editorRef.current.getValue() : formik.values.html;
    if (!validateRsvpTemplate(htmlValue)) return;
    const hasHtmlValidationErrors = htmlValidationErrors.some(m => m.severity === 8); // 8 is monaco.MarkerSeverity.Error

    if (hasHtmlValidationErrors) {
      notyf.error("Por favor, corrija os erros de validação no código HTML antes de salvar.");
      return;
    }

    if (isSystemMode) {
      const currentTemplate = { ...formik.values, html: htmlValue };
      const drafts = normalizeSystemTemplates({
        ...systemDrafts,
        [selectedSystemType]: currentTemplate,
      });
      const invalidTypes = [];

      for (const typeConfig of userTypes) {
        if (!validateRsvpTemplate(drafts[String(typeConfig.type)].html)) return;
        try {
          await validationSchema.validate(drafts[String(typeConfig.type)], { abortEarly: false });
        } catch (validationError) {
          invalidTypes.push(typeConfig.label);
        }
      }

      if (invalidTypes.length > 0) {
        formik.setFieldValue("html", htmlValue);
        formik.setTouched({
          name: true,
          subject: true,
          from_name: true,
          reply_to: true,
          html: true,
        });
        notyf.error(`Revise os campos obrigatórios dos modelos: ${invalidTypes.join(", ")}.`);
        return;
      }

      setSystemDrafts(drafts);
      onSave({ templates: drafts });
      return;
    }

    formik.setFieldValue("html", htmlValue);
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      formik.handleSubmit();
    } else {
      formik.setTouched({
        name: true,
        subject: true,
        from_name: true,
        reply_to: true,
        html: true,
      });
      if (errors.name) notyf.error(errors.name);
      else if (errors.subject) notyf.error(errors.subject);
      else if (errors.html) notyf.error(errors.html);
      else notyf.error("Por favor, preencha os campos obrigatórios corretamente.");
    }
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleRefreshPreview = () => {
    setPreviewVersion((currentVersion) => currentVersion + 1);
  };

  const showEditor = activePane === "split" || activePane === "editor";
  const showPreview = activePane === "split" || activePane === "preview";

  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  useEffect(() => {
    QRCode.toDataURL("https://r1eventech.com.br", { margin: 2, width: 160 }, (err, url) => {
      if (!err) setQrCodeDataUrl(url);
    });
  }, []);

  const previewApiBaseUrl = (domainConfig.restAPI || "").replace(/\/$/, "");
  const previewQrDownloadUrl = `${previewApiBaseUrl}/api/event/qrcode?url=${encodeURIComponent("https://r1eventech.com.br")}&margin=1&width=500&download=1&filename=${encodeURIComponent("qrcode-teste.png")}`;
  const previewDirectLoginUrl = `${(domainConfig.eventos || "https://r1eventech.com.br").replace(/\/$/, "")}/?token=${encodeURIComponent(dataTable?.token || "teste")}&loginCode=dXNlcj10ZXN0ZSZjb2RlPUFCQzEyMw%3D%3D`;
  const previewLogoUrl = form.logo ? templateLogoUrl : "";
  const previewHtml = form.html
    .replace(/\{\{logo\}\}/gi, previewLogoUrl)
    .replace(/\{\{qrcode_download_url\}\}/gi, previewQrDownloadUrl)
    .replace(/\{\{qrcode_with_user_access_code_url\}\}/gi, previewDirectLoginUrl)
    .replace(/\{\{qrcode_with_user_access_code_link\}\}/gi, previewDirectLoginUrl)
    .replace(/\{\{qrcode_with_user_access_code\}\}/gi, qrCodeDataUrl || "")
    .replace(/\{\{qrcode\}\}/gi, qrCodeDataUrl || "")
    .replace(/\{\{user_access_code\}\}/gi, "ABC123")
    .replace(/\{\{user_id\}\}/gi, "1")
    .replace(/\{\{email\}\}/gi, "participante@exemplo.com");
  const previewFrameKey = useMemo(
    () => `email-preview-${previewVersion}-${getPreviewContentHash(previewHtml)}`,
    [previewVersion, previewHtml],
  );

  return (
    <>
      <Modal
        show={show && !showRsvpManager}
        onHide={handleClose}
        fullscreen
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{isSystemMode ? "Modelos padrão de E-mail" : isEdit ? "Edição de modelo de e-mail" : "Criação de modelo de e-mail"}</Modal.Title>
        </Modal.Header>

        {/* ── Config bar ── */}
        <div
          className="border-bottom py-3"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <Row className="mx-0">
            {isSystemMode && (
              <Form.Group as={Col} xs={12} className="mb-3">
                <Form.Label className="mb-1">Tipo de usuário</Form.Label>
                <Form.Select
                  value={selectedSystemType}
                  onChange={(event) => handleSystemTypeChange(event.target.value)}
                >
                  {userTypes.map((typeConfig) => (
                    <option key={typeConfig.type} value={typeConfig.type}>
                      {typeConfig.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  {userTypes.find((typeConfig) => String(typeConfig.type) === selectedSystemType)?.description}
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group as={Col} xs={12} sm={6} md={6} className="mb-3 mb-sm-0">
              <Form.Label className="mb-1">
                Título do modelo
              </Form.Label>
              <Form.Control
                placeholder="Ex: Boas-vindas"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                isInvalid={formik.touched.name && !!formik.errors.name}
                onBlur={formik.handleBlur}
                name="name"
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.name}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} xs={12} sm={6} md={6}>
              <Form.Label className="mb-1">
                {isSystemMode ? "Modelos de e-mail" : "Copiar modelo de e-mail"}
              </Form.Label>
              <Select
                options={sourceTemplateOptions}
                value={sourceTemplateOptions.find((option) => option.value === selectedSourceTemplate) || null}
                placeholder={isSystemMode ? "Selecionar modelo..." : "Selecionar modelo para copiar..."}
                noOptionsMessage={() => "Nenhum modelo disponível para copiar"}
                isDisabled={saving || sourceTemplateOptions.length === 0}
                onChange={(opt) => {
                  if (!opt) return;

                  const applyTemplate = () => {
                    const nextValues = {
                      ...INITIAL,
                      template_id: template?.template_id ?? null,
                      template_version: template?.template_version ?? 0.0,
                    };
                    let nextRsvpList = [];
                    if (!isSystemMode) {
                      const source = opt.template;
                      if (!source) return;
                      const variables = JSON.parse(JSON.stringify(getTemplateVariablesObject(source.variables)));
                      Object.assign(nextValues, {
                        name: source.name || "",
                        subject: source.subject || "",
                        from_name: source.from_name || "",
                        reply_to: source.reply_to || "",
                        html: source.html || "",
                        qrcode: Boolean(source.qrcode),
                        logo: variables.logo || "",
                        variables: Object.keys(variables).length > 0 ? variables : null,
                      });
                      nextRsvpList = JSON.parse(JSON.stringify(normalizeRsvpMetadata(source.rsvp_metadata)));
                    } else if (opt.value !== "new") {
                      let finalHtml = DEFAULT_TEMPLATES[opt.value].html;
                      if (window.html_beautify && finalHtml) {
                        finalHtml = window.html_beautify(finalHtml, { indent_size: 2, preserve_newlines: true, max_preserve_newlines: 1 });
                      }
                      Object.assign(nextValues, {
                        name: DEFAULT_TEMPLATES[opt.value].nome,
                        subject: DEFAULT_TEMPLATES[opt.value].assunto,
                        from_name: DEFAULT_TEMPLATES[opt.value].fromName,
                        reply_to: DEFAULT_TEMPLATES[opt.value].replyTo,
                        html: finalHtml
                      });
                    }
                    setSelectedSourceTemplate(opt.value);
                    formik.setValues(nextValues);
                    formik.setTouched({});
                    setRsvpList(nextRsvpList);
                  };

                  const hasCurrentTemplateContent = ["name", "subject", "from_name", "reply_to", "html", "logo"]
                    .some((field) => String(formik.values[field] || "").trim().length > 0);

                  if (hasCurrentTemplateContent) {
                    confirmAlert({
                      closeOnEscape: false,
                      closeOnClickOutside: false,
                      customUI: ({ onClose }) => (
                        <ShowConfirm
                          title="Confirmar ação"
                          description="Isso substituirá todo o conteúdo atual do editor. Deseja continuar?"
                          action={() => {
                            applyTemplate();
                            onClose();
                          }}
                          onClose={onClose}
                        />
                      ),
                    });
                  } else {
                    applyTemplate();
                  }
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    padding: 5,
                  }),
                }}
              />
            </Form.Group>

            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <Button
                  variant="link"
                  className="p-0 text-decoration-none fw-semibold d-flex align-items-center gap-2 text-muted"
                  style={{ fontSize: 13, border: "none", color: "inherit" }}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  aria-controls="advanced-config-collapse"
                  aria-expanded={showAdvanced}
                >
                  Configurações avançadas de envio
                  <FontAwesomeIcon icon={showAdvanced ? faChevronUp : faChevronDown} />
                </Button>
              </div>
              <Collapse in={showAdvanced}>
                <Card.Body className="border">
                  <div id="advanced-config-collapse">
                    <Row className="mb-3">
                      <Form.Group as={Col} xs={12} sm={12} md={4} className="mb-3 mb-md-0">
                        <Form.Label className="mb-1">
                          Assunto do e-mail
                        </Form.Label>
                        <Form.Control
                          value={form.subject}
                          onChange={(e) => handleChange("subject", e.target.value)}
                          isInvalid={formik.touched.subject && !!formik.errors.subject}
                          onBlur={formik.handleBlur}
                          name="subject"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.subject}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group as={Col} xs={12} sm={6} md={4} className="mb-3 mb-md-0">
                        <Form.Label className="mb-1">
                          Nome do Remetente
                        </Form.Label>
                        <Form.Control
                          value={form.from_name}
                          onChange={(e) => handleChange("from_name", e.target.value)}
                          isInvalid={formik.touched.from_name && !!formik.errors.from_name}
                          onBlur={formik.handleBlur}
                          name="from_name"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.from_name}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group as={Col} xs={12} sm={6} md={4}>
                        <Form.Label className="mb-1">
                          Email Responsável (Reply-To)
                        </Form.Label>
                        <Form.Control
                          value={form.reply_to}
                          onChange={(e) => handleChange("reply_to", e.target.value)}
                          isInvalid={formik.touched.reply_to && !!formik.errors.reply_to}
                          onBlur={formik.handleBlur}
                          name="reply_to"
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.reply_to}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>

                    {!isSystemMode && (
                    <Row className="mb-3">
                      <Form.Group as={Col} md="6" className="mb-0">
                        <div>
                          <Form.Label>
                            Logo
                            <OverlayTrigger
                              overlay={<Tooltip>Faz upload de uma imagem fixa para usar no HTML do modelo de e-mail.</Tooltip>}
                            >
                              <HelpTooltip icon={faExclamationCircle} />
                            </OverlayTrigger>
                          </Form.Label>
                        </div>
                        <Card className="mb-0">
                          <Card.Body>
                            <Form.Control
                              type="hidden"
                              name="logo"
                              value={form.logo || ""}
                              onChange={formik.handleChange}
                            />
                            <div className="imagesUpload">
                              <img
                                className="h-100 img-thumbnail"
                                src={templateLogoUrl}
                                alt="Logo preview"
                              />
                            </div>
                            <DropzoneComponent
                              className="mt-3 dropzoneComp"
                              type="image"
                              handlePreviewSuccess={handleLogoPreviewSuccess}
                              handlePreviewError={handleLogoPreviewError}
                              setFieldValue={formik.setFieldValue}
                              folder="plataforma-eventos"
                              id={dataTable.evento_id}
                              field="logo"
                            />
                          </Card.Body>
                        </Card>
                      </Form.Group>
                    </Row>
                    )}
                    {!isSystemMode && (
                    <div
                      className="mt-2 mb-2"
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: 8,
                        overflow: "hidden",
                        fontSize: 12,
                      }}
                    >
                      <div
                        className="px-3 py-2 fw-semibold d-flex align-items-center justify-content-between"
                        style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}
                      >
                        <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
                          <FontAwesomeIcon icon={faLink} className="me-1" /> Placeholders de links RSVP
                        </span>
                        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                          <span className="text-muted" style={{ fontSize: 11 }}>{emailRsvpEntries.length} {emailRsvpEntries.length === 1 ? "entrada" : "entradas"}</span>
                          {canManageRsvps && <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ fontSize: 11, padding: "2px 8px" }}
                            disabled={saving || Boolean(pendingRsvpInsertionRef.current) || !dataTable?.evento_id}
                            onClick={() => setShowRsvpManager(true)}
                          ><FontAwesomeIcon icon={faCog} className="me-1" />Gerenciar RSVPs</Button>}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ fontSize: 11, padding: "2px 8px" }}
                            disabled={saving || Boolean(pendingRsvpInsertionRef.current) || !dataTable?.evento_id}
                            onClick={() => {
                              openRsvpPicker();
                            }}
                          >
                            <FontAwesomeIcon icon={faPlus} className="me-1" /> Adicionar link RSVP
                          </Button>
                        </div>
                      </div>
                      {rsvpRegistryError && <Alert variant="danger" className="m-2 p-2 d-flex align-items-center justify-content-between gap-2"><span>{rsvpRegistryError}</span><Button size="sm" variant="outline-danger" disabled={rsvpRegistryLoading} onClick={() => setRsvpRegistryReload((value) => value + 1)}>Tentar novamente</Button></Alert>}
                      {rsvpRegistryLoading && <div className="px-3 py-2 text-muted"><Spinner animation="border" size="sm" className="me-2" />Atualizando RSVPs...</div>}
                      {emailRsvpEntries.length === 0 ? (
                        <div className="px-3 py-3 text-center text-muted" style={{ fontSize: 12, backgroundColor: "#fff" }}>
                          Nenhum link RSVP adicionado a este modelo
                        </div>
                      ) : (
                        emailRsvpEntries.map((item, idx) => {
                          const placeholder = rsvpLinkPlaceholder(item.definition_id);
                          const htmlHasRsvpLink = form.html.includes(placeholder);
                          return (
                            <div
                              key={item.definition_id}
                              className="px-3 py-2 d-flex align-items-center gap-3"
                              style={{ borderBottom: idx < emailRsvpEntries.length - 1 ? "1px solid #f0f0f0" : "none", backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa" }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="fw-semibold text-truncate d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                                  <div className="text-truncate" style={{ fontSize: 11 }}>
                                    RSVP: <strong>{item.field}</strong>
                                  </div>
                                  {htmlHasRsvpLink && (
                                    <Badge bg="success" className="d-flex align-items-center gap-1" style={{ fontSize: 10, fontWeight: "normal" }}>
                                      <FontAwesomeIcon icon={faCheck} /> Adicionado
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
                                  <code>{placeholder}</code>
                                </div>
                              </div>
                              <div className="d-flex gap-2">
                                <Button variant="danger" size="sm" title="Remover link RSVP deste modelo" aria-label={`Remover link RSVP ${item.field} deste modelo`} disabled={saving} onClick={() => removeRsvp(item)}>
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                                <Button variant="primary" size="sm" onClick={async () => {
                                  try { await navigator.clipboard.writeText(placeholder); notyf.success("Placeholder RSVP copiado."); }
                                  catch (_) { notyf.error("Não foi possível copiar o placeholder RSVP."); }
                                }}>Copiar link</Button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    )}
                  </div>
                </Card.Body>
              </Collapse>
            </div>
          </Row>
        </div>

        {/* ── Toolbar: view toggle ── */}
        <div
          className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between gap-2" style={{ backgroundColor: "rgb(248, 249, 250)" }}
        >
          {/* View mode toggle */}
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${activePane === "editor" ? "btn-primary border-dark border" : "btn-outline-secondary"}`}
              onClick={() => {
                setActivePane("editor");
              }}
              title="Somente editor"
            >
              <FontAwesomeIcon icon={faCode} />
            </button>
            <button
              type="button"
              className={`d-none d-md-block btn ${activePane === "split" ? "btn-primary border-dark border" : "btn-outline-secondary"}`}
              onClick={() => {
                setActivePane("split");
              }}
              title="Tela dividida"
            >
              <FontAwesomeIcon icon={faTableColumns} />
            </button>
            <button
              type="button"
              className={`btn ${activePane === "preview" ? "btn-primary border-dark border" : "btn-outline-secondary"}`}
              onClick={() => setActivePane("preview")}
              title="Somente prévia"
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
          </div>
          <div className="ms-auto d-flex align-items-center gap-2">
            <Button
              variant="primary"
              title="Disparar e-mail teste"
              disabled={saving}
              onClick={async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length === 0) {
                  setShowTestModal(true);
                } else {
                  formik.setTouched({
                    name: true,
                    subject: true,
                    from_name: true,
                    reply_to: true,
                    html: true,
                  });
                  if (errors.subject || errors.from_name || errors.reply_to) {
                    setShowAdvanced(true);
                  }
                  notyf.error("Verifique os campos obrigatórios antes de enviar o e-mail teste.");
                }
              }}
            >
              <FontAwesomeIcon icon={faSend} />
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={saving}>{isSystemMode ? "Salvar todos os modelos" : "Salvar modelo"}</Button>
          </div>
        </div>

        {/* ── Split body ── */}
        <Modal.Body className="p-0" style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 750 }}>
          {/* Left – HTML Editor */}
          {showEditor && (
            <div className="border-right color-white"
              style={{
                flex: activePane === "split" ? "0 0 50%" : 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Editor titlebar */}
              <div
                className="d-flex flex-column flex-md-row px-3 py-2 align-items-start align-items-md-center border-bottom gap-2"
                style={{ backgroundColor: "rgb(248, 249, 250)", color: "inherit", flexShrink: 0, minHeight: 52 }}
              >
                <div className="d-flex align-items-center align-self-stretch" style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <div className="d-flex align-items-center flex-shrink-0 text-nowrap">
                    <FontAwesomeIcon icon={faCode} />
                    <span className="ms-2">Editor</span>
                  </div>

                  <div className="ms-auto d-flex align-items-center gap-2">
                    <button type="button" className="btn btn-sm btn-primary" title="Formatar HTML" aria-label="Formatar HTML" onClick={() => {
                      if (window.html_beautify) {
                        handleChange("html", window.html_beautify(form.html, { indent_size: 2, preserve_newlines: true, max_preserve_newlines: 1 }));
                      }
                    }}>
                      <FontAwesomeIcon icon={faMagic} />
                    </button>
                  </div>
                </div>

                <div className="align-self-stretch align-self-md-auto mt-2 mt-md-0" style={{ minWidth: '200px' }}>
                  <Form.Select
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      if (val === "__new_rsvp__") {
                        requestRsvpCreation();
                        e.target.value = "";
                        return;
                      }
                      insertVariable(val);
                      e.target.value = "";
                    }}
                    style={{ fontSize: 13, minHeight: 32 }}
                  >
                    <option value="">Variáveis</option>
                    {combinedVariables.map((opt, i) => (
                      <option key={`${opt.label}_${i}`} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <Editor
                height="100%"
                defaultLanguage="html"
                theme="vs-dark"
                value={form.html}
                onChange={(val) => handleChange("html", val || "")}
                onMount={handleEditorDidMount}
                options={{
                  wordWrap: "on",
                  minimap: { enabled: false },
                  formatOnPaste: true,
                  formatOnType: true,
                  tabSize: 2,
                  autoClosingTags: true,
                }}
              />
            </div>
          )
          }

          {/* Right – Preview */}
          {
            showPreview && (
              <div className="border-left"
                style={{
                  flex: activePane === "split" ? "0 0 50%" : 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  backgroundColor: "#e8eaed",
                }}
              >
                {/* Preview titlebar */}
                <div
                  className="d-flex px-3 py-2 align-items-center justify-content-between gap-2 border-bottom"
                  style={{ backgroundColor: "rgb(248, 249, 250)", color: "inherit", flexShrink: 0, minHeight: 52 }}
                >
                  <div>
                    <FontAwesomeIcon icon={faEye} />
                    <span className="ms-2">Prévia do e-mail</span>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    title="Atualizar prévia"
                    onClick={handleRefreshPreview}
                  >
                    <FontAwesomeIcon icon={faRefresh} />
                  </Button>
                </div>

                {/* Scrollable preview area */}
                <div style={{ flex: 1, overflowY: "auto", padding: "24px 16px" }}>
                  {form.html.trim() ? (
                    <div
                      style={{
                        margin: "0 auto",
                        background: "#fff",
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: "0 4px 24px rgba(0,0,0,.12)",
                        minHeight: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Fake email client header */}
                      <div
                        style={{
                          background: "#f1f3f5",
                          borderBottom: "1px solid #dee2e6",
                          padding: "12px 20px",
                          fontSize: 13,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background: "#3b82f6",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 700,
                              fontSize: 14,
                              flexShrink: 0,
                            }}
                          >
                            {(form.from_name || "E").charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{form.from_name || "Equipe do evento"}</div>
                            <div style={{ color: "#888", fontSize: 11 }}>Remetente técnico definido pelo serviço de e-mail</div>
                            <div style={{ color: "#666", fontSize: 11 }}>Respostas para: {form.reply_to || "não configurado"}</div>
                          </div>
                        </div>
                        <div style={{ color: "#222", fontWeight: 600 }}>
                          {form.subject || <span style={{ color: "#aaa" }}>(sem assunto)</span>}
                        </div>
                      </div>
                      {/* Email body */}
                      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <iframe
                          key={previewFrameKey}
                          title="Prévia do e-mail"
                          style={{ flex: 1, border: "none", width: "100%", height: "100%", minHeight: "500px" }}
                          srcDoc={previewHtml}
                          sandbox="allow-same-origin allow-popups"
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        margin: "0 auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 320,
                        color: "#aaa",
                        gap: 12,
                      }}
                    >
                      <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 40, opacity: 0.3 }} />
                      <p className="mb-0" style={{ fontSize: 14 }}>
                        A prévia aparecerá aqui conforme você escreve o HTML
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          }
        </Modal.Body >
      </Modal >

      {!isSystemMode && (
        <Modal
          show={show && showRsvpPicker}
          onHide={() => setShowRsvpPicker(false)}
          size="lg"
          centered
          scrollable
          className="nested-modal"
          style={{ zIndex: 1060 }}
          backdropClassName="nested-modal-backdrop"
        >
          <Modal.Header closeButton>
            <Modal.Title>Adicionar link RSVP</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">Selecione um RSVP existente para inserir o link neste modelo.</p>
            {rsvpRegistryError && (
              <Alert variant="danger" className="p-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <span>{rsvpRegistryError}</span>
                <Button
                  size="sm"
                  variant="outline-danger"
                  disabled={rsvpRegistryLoading}
                  onClick={() => setRsvpRegistryReload((value) => value + 1)}
                >
                  Tentar novamente
                </Button>
              </Alert>
            )}
            {rsvpRegistryLoading ? (
              <div className="text-center text-muted py-4">
                <Spinner animation="border" size="sm" className="me-2" />Carregando RSVPs...
              </div>
            ) : eventRsvpRegistry.definitions.length > 0 ? (
              <div className="list-group">
                {eventRsvpRegistry.definitions.map((settings) => {
                  const alreadyAdded = emailRsvpEntries.some((item) => item.definition_id === settings.definition_id);
                  return (
                    <div
                      key={settings.definition_id}
                      className="list-group-item d-flex align-items-center justify-content-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="fw-semibold text-break">{settings.name || "RSVP"}</div>
                        {settings.field_input_id && (
                          <small className="text-muted">Campo de resposta: {settings.field_input_id}</small>
                        )}
                        {settings.definition_id === eventRsvpRegistry.attendance_definition_id && (
                          <Badge bg="secondary" className="ms-2">Presença</Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant={alreadyAdded ? "outline-secondary" : "primary"}
                        disabled={saving || alreadyAdded || Boolean(pendingRsvpInsertionRef.current)}
                        onClick={() => insertSelectedRsvp(settings)}
                      >
                        {alreadyAdded ? "Já adicionado" : "Inserir link"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : !rsvpRegistryError ? (
              <div className="border rounded p-4 text-center text-muted">
                Nenhum RSVP disponível para este evento. Crie um RSVP para adicionar o link ao modelo.
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer className="justify-content-end">
            <Button
              variant="primary"
              disabled={saving || Boolean(pendingRsvpInsertionRef.current) || !dataTable?.evento_id}
              onClick={requestRsvpCreation}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Criar novo RSVP
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {!isSystemMode && dataTable?.evento_id && <EventRsvpSettingsModal
        key={`${dataTable.evento_id}:${rsvpCreationSequence}`}
        show={show && showRsvpCreation}
        onHide={() => setShowRsvpCreation(false)}
        eventId={dataTable.evento_id}
        eventTimezone={dataTable.timezone || "America/Sao_Paulo"}
        registrationEndDate={dataTable.json?.form?.register_end_date}
        nested
        onSaved={(saved) => {
          pendingRsvpInsertionRef.current = { eventId: dataTable.evento_id, definitionId: saved.definition_id };
          setRsvpRegistryReload((value) => value + 1);
          onRsvpChanged?.();
        }}
      />}

      {canManageRsvps && dataTable?.evento_id && <EventRsvpManagerModal
        show={show && showRsvpManager}
        onHide={() => setShowRsvpManager(false)}
        eventId={dataTable.evento_id}
        eventTimezone={dataTable.timezone || "America/Sao_Paulo"}
        registrationEndDate={dataTable.json?.form?.register_end_date}
        onRegistryChange={setEventRsvpRegistry}
        onSaved={() => {
          setRsvpRegistryReload((value) => value + 1);
          onRsvpChanged?.();
        }}
      />}

      {/* Test Email Modal */}
      <TestEmailModal
        show={showTestModal}
        eventId={isSystemMode ? undefined : dataTable?.evento_id}
        onHide={() => setShowTestModal(false)}
        templateHtml={form.html}
        logoUrl={form.logo ? templateLogoUrl : ""}
        subject={form.subject}
        fromName={form.from_name}
        replyTo={form.reply_to}
        notyf={notyf}
        validateRsvpTemplate={validateRsvpTemplate}
      />
    </>
  );
};

// ─── TestEmailModal ───────────────────────────────────────────────────────────
const TestEmailModal = ({ show, onHide, eventId, templateHtml, logoUrl, subject, fromName, replyTo, notyf, validateRsvpTemplate }) => {
  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Insira um endereço de e-mail válido")
        .required("E-mail é obrigatório"),
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      if (!validateRsvpTemplate(templateHtml)) {
        setSubmitting(false);
        return;
      }
      setSubmitting(true);
      axios
        .post("/api/event/email-templates/test", {
          to: values.email,
          subject: subject || "(sem assunto)",
          html: templateHtml,
          event_id: eventId,
          logo_url: logoUrl || undefined,
          from_name: fromName || undefined,
          reply_to: replyTo || undefined,
        })
        .then(() => {
          notyf.success(`E-mail de teste enviado para ${values.email}`);
          resetForm();
          onHide();
        })
        .catch((err) => {
          const msg = err?.response?.data?.message || "Erro ao enviar e-mail de teste";
          notyf.error(msg);
        })
        .finally(() => setSubmitting(false));
    },
  });

  return (
    <Modal show={show} onHide={() => { formik.resetForm(); onHide(); }} centered backdrop="static" className="nested-modal" backdropClassName="nested-modal-backdrop">
      <Modal.Header closeButton>
        <Modal.Title>Disparar E-mail Teste</Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          {/\{\{rsvp_link_[A-Za-z0-9_-]+\}\}/.test(templateHtml || "") && <Alert variant="info" className="small p-2">Os links RSVP abrirão uma prévia válida por 24 horas. As escolhas na prévia são simuladas e não alteram respostas de participantes.</Alert>}
          <Form.Group>
            <Form.Label>Destinatário de Teste</Form.Label>
            <Form.Control
              type="email"
              name="email"
              placeholder="exemplo@email.com"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.email && !!formik.errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.email}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { formik.resetForm(); onHide(); }}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={formik.isSubmitting}>
            Enviar Teste
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EmailTemplateEditorModal;
